import { useEffect, useState, useSyncExternalStore } from "react";
import { DropResult } from "@hello-pangea/dnd";
import { useTranslation } from "@localization/useTranslation";
import { usePane } from "../../../../main/panes/paneLayout/usePane";
import { TableDisplayField, TableDisplayFieldsPaneProps } from "./TableDisplayFieldsPane.types";
import { areSameFields, moveField, onlyAvailableFields, toggleField } from "./TableDisplayFieldsPane.utils";

interface PendingFields {
    editedFromListFields: Array<string>;
    fields: Array<string>;
}

export function useTableDisplayFieldsPane(props: TableDisplayFieldsPaneProps) {
    const { translateString } = useTranslation();
    const { close, setHasUnsavedChanges } = usePane();
    const listProps = useSyncExternalStore(props.store.subscribe, props.store.getListProps);
    const [searchInput, setSearchInput] = useState("");
    const [pending, setPending] = useState<PendingFields>();

    const listFields = onlyAvailableFields(listProps.selectedFields, listProps.options);
    // Switching view while the pane is open replaces the list's columns. Ticks made against the columns that were
    // there before mean nothing for the new view, so they are dropped and the pane shows the new columns.
    const pendingFields = pending && areSameFields(pending.editedFromListFields, listFields) ? pending.fields : listFields;
    const hasPendingChanges = !areSameFields(pendingFields, listFields);

    // Every render rather than on each edit, because the flag also has to drop when a view switch drops the
    // pending columns. Setting it is a plain assignment in the pane store.
    useEffect(() => {
        setHasUnsavedChanges(hasPendingChanges);
    });

    const resetFields = onlyAvailableFields(listProps.savedViewFields ?? [], listProps.options);
    const canReset = !areSameFields(pendingFields, resetFields);

    const searchTerm = searchInput.toLowerCase();
    const fields: Array<TableDisplayField> = listProps.options.map((option) => ({
        id: option.id,
        name: translateString(option.name),
        tooltip: option.tooltip,
        grouping: option.grouping,
        isMandatory: option.mandatory ?? false,
    }));
    const matchingFields = fields.filter((field) => field.name.toLowerCase().includes(searchTerm));

    const shownFields = pendingFields.flatMap((fieldId) => matchingFields.filter((field) => field.id === fieldId));
    const hiddenFieldGroups = listProps.groupingOrder
        .map((grouping) => ({
            grouping,
            name: translateString(grouping),
            fields: matchingFields.filter((field) => field.grouping === grouping && !pendingFields.includes(field.id)),
        }))
        .filter((group) => group.fields.length > 0);

    function setPendingFields(fieldIds: Array<string>) {
        setPending({ editedFromListFields: listFields, fields: fieldIds });
    }

    function onDragEnd(result: DropResult) {
        if (!result.destination || result.destination.index === result.source.index) {
            return;
        }

        setPendingFields(moveField(pendingFields, result.draggableId, shownFields[result.destination.index].id));
    }

    function getResetText() {
        if (!canReset) {
            return undefined;
        }

        return listProps.isCustomView ? translateString("resetFilterToSavedView") : translateString("resetToDefaultView");
    }

    return {
        searchInput,
        setSearchInput,
        shownFields,
        hiddenFieldGroups,
        renderSelectedField: listProps.renderSelectedField,
        onFieldToggle: (fieldId: string) => setPendingFields(toggleField(pendingFields, fieldId)),
        onDragEnd,
        resetText: getResetText(),
        onResetClick: () => setPendingFields(resetFields),
        onApplyClick: () => {
            listProps.onSave(pendingFields);
            close();
        },
    };
}
